import React, { useEffect, useId, useRef, useState } from "react";
import PublicRaceRanking from "./PublicRaceRanking";
import { useFishingDiscipline, formatKg } from "../../types/fishingDiscipline";
import { PublicApiService } from "../../services/publiApiService";

export interface CompetitionCardProps {
  id: number;
  name: string;
  league: string;
  date: string;
  location: string;
  type: string;
  description: string;
  urlLocandina?: string | null;
  resultsPublished?: boolean;
}

const CompetitionCard: React.FC<CompetitionCardProps> = ({
  id,
  name,
  league,
  date,
  location,
  type,
  description,
  urlLocandina,
  resultsPublished,
}) => {
  const discipline = useFishingDiscipline();
  const [showRanking, setShowRanking] = useState(false);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [rankingError, setRankingError] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (!showRanking) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowRanking(false);
      if (event.key !== "Tab") return;
      const elements = dialogRef.current?.querySelectorAll<HTMLElement>('button, a[href], [tabindex="0"]');
      if (!elements?.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [showRanking]);

  const today = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Rome" }).format(new Date());
  const hasValidDate = /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date));
  const isFuture = hasValidDate && date > today;
  const isConcluded = Boolean(resultsPublished) || (hasValidDate && date < today);

  return (
    <article className="race-card">
      <div className="race-card__topline">
        <time dateTime={date} className="race-card__date">{hasValidDate ? new Date(`${date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "Data da definire"}</time>
        {isConcluded && <span className="race-card__status">Conclusa</span>}
      </div>
      <div>
        <h2>{name}</h2>
        {league && <p className="race-card__league">{league}</p>}
      </div>
      <dl className="race-card__meta">
        {location && <div><dt>Luogo</dt><dd>{location}</dd></div>}
        {type && <div><dt>Formato</dt><dd>{type}</dd></div>}
      </dl>
      {description && <p className="race-card__description">{description}</p>}
      {/* Bottone classifica o iscriviti */}
      <div className="race-card__actions">
      {urlLocandina && (
        <a
          href={urlLocandina}
          target="_blank"
          rel="noopener noreferrer"
          className="site-button site-button--secondary"
        >
          Locandina
        </a>
      )}
      {isFuture && !isConcluded ? (
          <a
            href={`https://wa.me/393926556115?text=${encodeURIComponent(`Vorrei iscrivermi alla gara ${name}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="site-button"
            title="Iscriviti alla gara su WhatsApp"
          >
            Iscriviti
          </a>
      ) : (
        <button
          type="button"
          className="site-button"
          onClick={async () => {
            setShowRanking(true);
            setLoadingLeaderboard(true);
            setRankingError("");
            try {
              const data = await PublicApiService.getLeaderboard(id);
              setLeaderboard(data);
            } catch (err) {
              setLeaderboard([]);
              setRankingError("Impossibile caricare la classifica. Chiudi e riprova.");
            } finally {
              setLoadingLeaderboard(false);
            }
          }}
          title="Vedi classifica gara"
        >
          Classifica
        </button>
      )}
      </div>
      {/* Modale classifica */}
      {showRanking && (
        <div
          className="site-dialog-backdrop"
          onClick={() => setShowRanking(false)}
        >
          <div
            ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={headingId}
            className="site-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="site-dialog__header">
            <div><h3 id={headingId}>Classifica gara</h3><p>{name}</p></div>
            <button type="button"
              className="site-dialog__close" aria-label="Chiudi classifica"
              onClick={() => setShowRanking(false)}
              title="Chiudi"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            </div>
            {loadingLeaderboard ? (
              <p role="status" className="site-notice">Caricamento classifica…</p>
            ) : rankingError ? <p role="alert" className="site-notice site-notice--error">{rankingError}</p> : leaderboard.length === 0 ? (
              <div className="site-notice">
                Nessun risultato disponibile.
              </div>
            ) : (
              discipline === 2 ? <PublicRaceRanking valueLabel="Peso pescato" entries={leaderboard.map(item => ({
                id: item.competitorId, name: `${item.lastName} ${item.firstName}`, position: item.position,
                value: item.isAbsent ? "—" : `${formatKg(item.weightKg ?? 0)} kg`, isAbsent: item.isAbsent,
              }))} /> :
              // Raggruppa per sectionId e, se necessario, per groupId
              Object.entries(
                leaderboard.reduce((acc: any, item: any) => {
                  const sectionId = item.sectionName || "Sconosciuta";
                  if (!acc[sectionId]) acc[sectionId] = [];
                  acc[sectionId].push(item);
                  return acc;
                }, {} as Record<string, any[]>)
              )
                .sort(([a], [b]) => a.localeCompare(b, "it", { numeric: true }))
                .map(([sectionId, items], sectionIdx) => {
                  let arr = items as any[];
                  // Raggruppa per groupId se almeno un item ha groupId valorizzato
                  if (
                    arr.some(
                      (item) =>
                        item.groupId !== undefined && item.groupId !== null
                    )
                  ) {
                    const groupMap: { [groupId: number]: any[] } = {};
                    arr.forEach((item) => {
                      if (item.groupId === undefined || item.groupId === null)
                        return;
                      if (!groupMap[item.groupId]) groupMap[item.groupId] = [];
                      groupMap[item.groupId].push(item);
                    });
                    arr = Object.values(groupMap).map((groupItems) => {
                      const position = Math.min(
                        ...groupItems.map((i) => i.position)
                      );
                      const trote = groupItems.reduce(
                        (sum, i) => sum + (i.totalFishCount ?? 0),
                        0
                      );
                      return {
                        id: groupItems[0].groupId,
                        members: groupItems.map((i) => ({
                          lastName: i.lastName,
                          firstName: i.firstName,
                        })),
                        totalFishCount: trote,
                        score: groupItems[0].score,
                        position,
                        sectionName: groupItems[0].sectionName,
                      };
                    });
                  }
                  return (
                    <div key={sectionId} className="mb-6">
                      <div className="font-semibold text-lg text-black-700 mb-2">
                        {arr[0]?.sectionName}
                      </div>
                      <PublicRaceRanking valueLabel="Trote pescate" entries={arr
                        .sort((a: any, b: any) => a.position - b.position)
                        .map((item: any, index: number) => ({
                          id: item.id || item.competitorId || index,
                          name: item.members ? item.members.map((member: any) => `${member.lastName} ${member.firstName}`).join(" / ") : `${item.lastName} ${item.firstName}`,
                          position: index + 1, value: `${item.totalFishCount ?? 0} trote`, points: item.score ?? 0,
                        }))} />
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default CompetitionCard;
